import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Assessment as AssessmentIcon,
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  CreditCard as CreditCardIcon,
  Edit as EditIcon,
  Hub as HubIcon,
  People as PeopleIcon,
  Public as PublicIcon,
  ReceiptLong as ReceiptIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  School as SchoolIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import SessionActionsDialog from '../interviews/SessionActionsDialog';
import SessionFeedbackDialog from '../interviews/SessionFeedbackDialog';
import SessionReportDialog from '../interviews/SessionReportDialog';
import { ManageableSession } from '../interviews/types';

const STORAGE_KEY = 'superadmin_company_hub_company_id';
const SHOW_ALL_STORAGE_KEY = 'superadmin_company_hub_show_all';

/** Sticky first columns while the rest of the interviews table scrolls horizontally. */
const STICKY_ID_WIDTH = 72;
const STICKY_CANDIDATE_LEFT = STICKY_ID_WIDTH;
const stickyIdCellSx = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  minWidth: STICKY_ID_WIDTH,
  maxWidth: STICKY_ID_WIDTH,
  width: STICKY_ID_WIDTH,
  backgroundColor: 'var(--bg-surface)',
  'tr.MuiTableRow-hover:hover > &': { backgroundColor: 'action.hover' },
} as const;
const stickyCandidateCellSx = {
  position: 'sticky',
  left: STICKY_CANDIDATE_LEFT,
  zIndex: 2,
  minWidth: 200,
  width: 200,
  backgroundColor: 'var(--bg-surface)',
  'tr.MuiTableRow-hover:hover > &': { backgroundColor: 'action.hover' },
} as const;
const stickyIdHeadSx = {
  ...stickyIdCellSx,
  zIndex: 3,
  backgroundColor: 'var(--neutral-50)',
  'tr.MuiTableRow-hover:hover > &': { backgroundColor: 'var(--neutral-50)' },
} as const;
const stickyCandidateHeadSx = {
  ...stickyCandidateCellSx,
  zIndex: 3,
  backgroundColor: 'var(--neutral-50)',
  'tr.MuiTableRow-hover:hover > &': { backgroundColor: 'var(--neutral-50)' },
} as const;

interface CompanyOption {
  id: number;
  name: string;
  status?: string;
  latest_expiry_date?: string | null;
  remaining_credits?: number;
}

interface OverviewUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type: string;
  is_active: boolean;
  is_archived: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  department: number | null;
  date_joined: string | null;
  last_login: string | null;
  profile: { mobile?: string; country_code?: string; aadhaar_number?: string | null } | null;
}

interface RelationshipRow {
  id: number;
  role: string;
  usermanager: boolean;
  added_at: string;
  user: OverviewUser;
}

interface InterviewRow {
  id: number;
  candidate: number | null;
  candidate_email: string | null;
  candidate_name: string | null;
  status: string;
  started_at: string;
  ended_at: string | null;
  scheduled_end_time?: string | null;
  duration_minutes?: number | null;
  actual_duration_minutes?: number | null;
  total_score: number | null;
  effective_score: number | null;
  minimum_questions_for_scoring?: number | null;
  job_role: string;
  invite_based?: boolean;
  job_description_id: number | null;
  job_description_title: string | null;
  is_archived: boolean;
  archived_at?: string | null;
  credits_used: number;
  tags?: string[];
  language_code?: string | null;
  language_name?: string | null;
  question_count?: number | null;
  verification_status?: string | null;
  verification_similarity_score?: number | null;
  prefer_conceptual_only?: boolean;
  browser?: string | null;
  question_video_count?: number;
  has_session_recording?: boolean;
  has_feedback?: boolean;
  questions_total?: number;
  unscored_question_count?: number;
}

interface JobRow {
  id: number;
  title: string;
  status: string;
  jd_type: string;
  is_public: boolean;
  department: number;
  department_name: string;
  location: string | null;
  experience_level: string | null;
  employment_type: string | null;
  created_at: string;
  updated_at: string;
  matching_status: string | null;
  scheduling_status: string | null;
}

interface CreditTxRow {
  id: number;
  transaction_type: string;
  amount: number;
  timestamp: string;
  notes: string | null;
  is_expired: boolean;
  expiry_date: string | null;
}

interface CompanyOverview {
  id: number;
  name: string;
  email: string;
  status: string;
  subscription_plan: string;
  total_credits?: number;
  used_credits?: number;
  remaining_credits?: number;
}

interface DataOverview {
  company: CompanyOverview;
  company_admins: RelationshipRow[];
  candidates: RelationshipRow[];
  employees: RelationshipRow[];
  dependents: unknown[];
  dependents_note: string;
  /** Small recent sample for Overview widgets — not the full list. */
  interview_sessions: InterviewRow[];
  /** True total for this company (not capped). */
  interview_sessions_count: number;
  interview_sessions_completed_count?: number;
  job_descriptions: JobRow[];
  credit_transactions: CreditTxRow[];
  credits_summary: {
    total_credits: number;
    used_credits: number;
    remaining_credits: number;
  } | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatLabel = (value?: string | null) =>
  value
    ? value
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim()
    : 'Unknown';

const formatExpiryDate = (value?: string | null) => {
  if (!value) return 'No expiry date';
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const pad2 = (n: number) => String(n).padStart(2, '0');

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
};

const splitDateTime = (value?: string | null): { date: string; time: string } | null => {
  const d = parseDate(value);
  if (!d) return null;
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yy = pad2(d.getFullYear() % 100);
  let hours = d.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return {
    date: `${mm}/${dd}/${yy}`,
    time: `${pad2(hours)}:${pad2(d.getMinutes())} ${ampm}`,
  };
};

const formatDateTime = (value?: string | null) => {
  const parts = splitDateTime(value);
  return parts ? `${parts.date} at ${parts.time}` : '—';
};

const formatScore = (value?: number | null) =>
  value != null && Number.isFinite(value) ? `${value.toFixed(1)}%` : '—';

const formatDurationClock = (totalSeconds?: number | null) => {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) return '—';
  const rounded = Math.round(totalSeconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const seconds = rounded % 60;
  return hours > 0
    ? `${hours}h ${pad2(minutes)}m ${pad2(seconds)}s`
    : `${pad2(minutes)}m ${pad2(seconds)}s`;
};

const getElapsedSeconds = (row: InterviewRow): number | null => {
  const start = parseDate(row.started_at);
  const end = parseDate(row.ended_at);
  if (start && end) {
    const seconds = (end.getTime() - start.getTime()) / 1000;
    if (seconds > 0) return seconds;
  }
  if (row.actual_duration_minutes != null && Number.isFinite(row.actual_duration_minutes)) {
    return row.actual_duration_minutes * 60;
  }
  return null;
};

const getScoreChipColor = (value?: number | null): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  if (value == null || !Number.isFinite(value)) return 'default';
  if (value >= 80) return 'success';
  if (value >= 60) return 'info';
  if (value >= 40) return 'warning';
  return 'error';
};

const getInterviewStatusColor = (status?: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  const normalized = status?.toLowerCase();
  if (normalized === 'completed') return 'success';
  if (normalized === 'in_progress') return 'info';
  if (normalized === 'pending') return 'warning';
  if (normalized === 'cancelled') return 'error';
  return 'default';
};

const getStatusChipColor = (status?: string): 'success' | 'warning' | 'error' | 'default' => {
  const normalized = status?.toLowerCase();
  if (normalized === 'active') return 'success';
  if (normalized === 'suspended') return 'warning';
  if (normalized === 'inactive') return 'default';
  return 'default';
};

const toManageableSession = (row: InterviewRow): ManageableSession => ({
  id: row.id,
  status: row.status,
  candidate_name: row.candidate_name,
  candidate_email: row.candidate_email,
  job_role: row.job_role,
  job_description_title: row.job_description_title,
  effective_score: row.effective_score,
  question_video_count: row.question_video_count ?? 0,
  has_session_recording: row.has_session_recording ?? false,
  has_feedback: row.has_feedback ?? false,
  questions_total: row.questions_total ?? 0,
  unscored_question_count: row.unscored_question_count ?? 0,
});

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const CompanyHub: React.FC = () => {
  const navigate = useNavigate();

  // Core Data State
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
  const [showAllCompanies, setShowAllCompanies] = useState<boolean>(() => {
    return localStorage.getItem(SHOW_ALL_STORAGE_KEY) === 'true';
  });
  const [overview, setOverview] = useState<DataOverview | null>(null);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tab State: 0 = Overview, 1 = People, 2 = Interviews, 3 = Jobs, 4 = Credits
  const [activeTab, setActiveTab] = useState(0);

  // Modals & Action State
  const [companyDialog, setCompanyDialog] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    status: 'active',
    email: '',
    subscription_plan: 'basic',
  });
  const [savingCompany, setSavingCompany] = useState(false);

  const [userDialog, setUserDialog] = useState(false);
  const [userEdit, setUserEdit] = useState<RelationshipRow | null>(null);
  const [userForm, setUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    is_active: true,
    usermanager: false,
  });
  const [savingUser, setSavingUser] = useState(false);

  const [jdDialog, setJdDialog] = useState(false);
  const [jdEdit, setJdEdit] = useState<JobRow | null>(null);
  const [jdForm, setJdForm] = useState({ title: '', status: 'open' });
  const [savingJd, setSavingJd] = useState(false);

  const [sessionDialog, setSessionDialog] = useState(false);
  const [sessionEdit, setSessionEdit] = useState<InterviewRow | null>(null);
  const [sessionForm, setSessionForm] = useState({ status: 'pending', is_archived: false });
  const [savingSession, setSavingSession] = useState(false);

  // Credits Modals
  const [creditDialog, setCreditDialog] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditNotes, setCreditNotes] = useState('');
  const [creditExpiryDate, setCreditExpiryDate] = useState('');
  const [savingCredits, setSavingCredits] = useState(false);

  const [decreaseCreditDialog, setDecreaseCreditDialog] = useState(false);
  const [decreaseCreditAmount, setDecreaseCreditAmount] = useState('');
  const [decreaseCreditNotes, setDecreaseCreditNotes] = useState('');
  const [decreaseSourceTransactionId, setDecreaseSourceTransactionId] = useState<number | ''>('');
  const [savingDecreaseCredits, setSavingDecreaseCredits] = useState(false);

  // Inspection Drawer & Session Sub-dialogs
  const [detailSession, setDetailSession] = useState<InterviewRow | null>(null);
  const [actionsFor, setActionsFor] = useState<ManageableSession | null>(null);
  const [reportFor, setReportFor] = useState<ManageableSession | null>(null);
  const [feedbackFor, setFeedbackFor] = useState<ManageableSession | null>(null);
  const [notice, setNotice] = useState<{ message: string; severity: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  // Sub-filter & Search states for tabs
  const [peopleSearch, setPeopleSearch] = useState('');
  const [peopleFilter, setPeopleFilter] = useState<'all' | 'admin' | 'employee' | 'candidate' | 'dependents'>('all');

  const [interviewSearch, setInterviewSearch] = useState('');
  const [interviewStatusFilter, setInterviewStatusFilter] = useState<string>('all');
  const [interviewPage, setInterviewPage] = useState(0);
  const [interviewRowsPerPage, setInterviewRowsPerPage] = useState(20);
  const [interviewRows, setInterviewRows] = useState<InterviewRow[]>([]);
  const [interviewTotalCount, setInterviewTotalCount] = useState(0);
  const [interviewLoading, setInterviewLoading] = useState(false);
  const [interviewListError, setInterviewListError] = useState<string | null>(null);
  // Bumped after edit/recovery so the paginated Interviews table refetches.
  const [interviewListRefreshKey, setInterviewListRefreshKey] = useState(0);

  const [jdSearch, setJdSearch] = useState('');
  const [creditSearch, setCreditSearch] = useState('');

  const getDefaultExpiryDate = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().split('T')[0];
  };

  const fetchCompanies = useCallback(async (all: boolean = false, preferredCompanyId?: number) => {
    setLoadingCompanies(true);
    try {
      const endpoint = all
        ? '/super-admin/companies/?page_size=500'
        : '/super-admin/companies/?status=active&page_size=500';
      const res = await api.get(endpoint);
      const rawList: CompanyOption[] = res.data.results || res.data || [];
      const list = all
        ? rawList
        : rawList.filter(
            (c: CompanyOption) => !c.status || c.status.toLowerCase() === 'active'
          );
      setCompanies(list);

      const stored = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
      const targetId = preferredCompanyId ?? (stored ? parseInt(stored, 10) : null);
      if (targetId) {
        let found = list.find((c: CompanyOption) => c.id === targetId);
        if (found) {
          setSelectedCompany(found);
          localStorage.setItem(STORAGE_KEY, String(found.id));
          sessionStorage.removeItem(STORAGE_KEY);
        } else {
          // If not in the current list, try fetching the company directly by ID
          try {
            const singleRes = await api.get(`/super-admin/companies/${targetId}/`);
            const singleComp = singleRes.data;
            if (singleComp) {
              if (!singleComp.status || singleComp.status.toLowerCase() === 'active') {
                setCompanies((prev) => [singleComp, ...prev.filter((c) => c.id !== singleComp.id)]);
                setSelectedCompany(singleComp);
                localStorage.setItem(STORAGE_KEY, String(singleComp.id));
                sessionStorage.removeItem(STORAGE_KEY);
              } else {
                setShowAllCompanies(true);
                localStorage.setItem(SHOW_ALL_STORAGE_KEY, 'true');
                const allRes = await api.get('/super-admin/companies/?page_size=500');
                const allList: CompanyOption[] = allRes.data.results || allRes.data || [];
                setCompanies(allList);
                const foundInAll = allList.find((c: CompanyOption) => c.id === targetId) || singleComp;
                setSelectedCompany(foundInAll);
                localStorage.setItem(STORAGE_KEY, String(foundInAll.id));
                sessionStorage.removeItem(STORAGE_KEY);
              }
            }
          } catch {
            localStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(STORAGE_KEY);
            setSelectedCompany(null);
            setOverview(null);
          }
        }
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load companies');
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  const handleToggleShowAll = () => {
    const nextShowAll = !showAllCompanies;
    setShowAllCompanies(nextShowAll);
    localStorage.setItem(SHOW_ALL_STORAGE_KEY, String(nextShowAll));
    fetchCompanies(nextShowAll, selectedCompany?.id);
    setNotice({
      message: nextShowAll
        ? 'Now showing all companies (active, suspended, and inactive).'
        : 'Filtered to active companies only.',
      severity: 'info',
    });
  };

  const fetchOverview = useCallback(async (companyId: number) => {
    setLoadingOverview(true);
    setError(null);
    try {
      const res = await api.get(`/super-admin/companies/${companyId}/data-overview/`);
      if (!showAllCompanies && res.data?.company?.status && res.data.company.status.toLowerCase() !== 'active') {
        setError(`Company "${res.data.company.name}" is ${res.data.company.status}. Company Hub is currently set to active companies only.`);
        setSelectedCompany(null);
        setOverview(null);
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
        return;
      }
      setOverview(res.data);
    } catch (e: any) {
      setError(
        e.response?.data?.detail ||
          e.response?.data?.error ||
          e.message ||
          'Failed to load company data'
      );
      setOverview(null);
    } finally {
      setLoadingOverview(false);
    }
  }, [showAllCompanies]);

  useEffect(() => {
    fetchCompanies(showAllCompanies);
  }, [fetchCompanies]);

  useEffect(() => {
    if (selectedCompany) {
      localStorage.setItem(STORAGE_KEY, String(selectedCompany.id));
      sessionStorage.removeItem(STORAGE_KEY);
      fetchOverview(selectedCompany.id);
    } else {
      setOverview(null);
    }
  }, [selectedCompany, fetchOverview]);

  // Handlers
  const openCompanyEdit = () => {
    if (!overview) return;
    setCompanyForm({
      name: overview.company.name,
      status: overview.company.status || 'active',
      email: overview.company.email || '',
      subscription_plan: overview.company.subscription_plan || 'basic',
    });
    setCompanyDialog(true);
  };

  const saveCompany = async () => {
    if (!selectedCompany) return;
    setSavingCompany(true);
    try {
      await api.patch(`/super-admin/companies/${selectedCompany.id}/`, companyForm);
      setCompanyDialog(false);
      if (!showAllCompanies && companyForm.status !== 'active') {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
        setSelectedCompany(null);
        setOverview(null);
        setNotice({
          message: `Company marked as ${companyForm.status} and removed from Company Hub.`,
          severity: 'info',
        });
        await fetchCompanies(showAllCompanies);
      } else {
        await fetchOverview(selectedCompany.id);
        await fetchCompanies(showAllCompanies, selectedCompany.id);
      }
    } catch (e: any) {
      alert(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSavingCompany(false);
    }
  };

  const openUserEdit = (row: RelationshipRow) => {
    setUserEdit(row);
    setUserForm({
      first_name: row.user.first_name,
      last_name: row.user.last_name,
      email: row.user.email,
      is_active: row.user.is_active,
      usermanager: row.usermanager,
    });
    setUserDialog(true);
  };

  const saveUser = async () => {
    if (!userEdit || !selectedCompany) return;
    setSavingUser(true);
    try {
      const ut = userEdit.user.user_type;
      const u = userEdit.user;
      const submitData: Record<string, unknown> = {
        username: u.username,
        first_name: userForm.first_name,
        last_name: userForm.last_name,
        email: userForm.email,
        is_active: userForm.is_active,
        user_type: ut,
        is_staff: u.is_staff,
        is_superuser: u.is_superuser,
        department: u.department,
      };
      if (ut === 'company_admin') {
        submitData.company_id = selectedCompany.id;
        submitData.usermanager = userForm.usermanager;
      }
      await api.put(`/super-admin/users/${userEdit.user.id}/`, submitData);
      setUserDialog(false);
      fetchOverview(selectedCompany.id);
    } catch (e: any) {
      alert(e.response?.data?.error || e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSavingUser(false);
    }
  };

  const openJdEdit = (row: JobRow) => {
    setJdEdit(row);
    setJdForm({ title: row.title, status: row.status });
    setJdDialog(true);
  };

  const saveJd = async () => {
    if (!jdEdit || !selectedCompany) return;
    setSavingJd(true);
    try {
      await api.patch(`/job-descriptions/${jdEdit.id}/`, jdForm);
      setJdDialog(false);
      fetchOverview(selectedCompany.id);
    } catch (e: any) {
      alert(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSavingJd(false);
    }
  };

  const openSessionEdit = (row: InterviewRow) => {
    setSessionEdit(row);
    setSessionForm({ status: row.status, is_archived: row.is_archived });
    setSessionDialog(true);
  };

  const saveSession = async () => {
    if (!sessionEdit || !selectedCompany) return;
    setSavingSession(true);
    try {
      await api.patch(`/sessions/${sessionEdit.id}/`, sessionForm);
      setSessionDialog(false);
      setInterviewListRefreshKey((k) => k + 1);
      fetchOverview(selectedCompany.id);
    } catch (e: any) {
      alert(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSavingSession(false);
    }
  };

  const addCredits = async () => {
    if (!selectedCompany) return;
    const amount = parseInt(creditAmount, 10);
    if (!amount || amount < 1) {
      alert('Enter a positive credit amount');
      return;
    }
    if (!creditExpiryDate) {
      alert('Expiry date is required');
      return;
    }
    setSavingCredits(true);
    try {
      await api.post(`/super-admin/companies/${selectedCompany.id}/add_credits/`, {
        amount,
        notes: creditNotes || 'Super Admin — Company Hub',
        expiry_date: creditExpiryDate,
      });
      setCreditDialog(false);
      setCreditAmount('');
      setCreditNotes('');
      setCreditExpiryDate('');
      fetchOverview(selectedCompany.id);
    } catch (e: unknown) {
      const data = (e as { response?: { data?: Record<string, unknown> | string } }).response?.data;
      if (data && typeof data === 'object') {
        const errMsg = data.error;
        alert(typeof errMsg === 'string' ? errMsg : JSON.stringify(data));
      } else {
        alert(e instanceof Error ? e.message : 'Failed to add credits');
      }
    } finally {
      setSavingCredits(false);
    }
  };

  const decreaseCredits = async () => {
    if (!selectedCompany) return;
    const amount = parseInt(decreaseCreditAmount, 10);
    if (!amount || amount < 1) {
      alert('Enter a positive credit amount to decrease');
      return;
    }
    if (!decreaseSourceTransactionId) {
      alert('Select a source credit addition transaction');
      return;
    }
    if (!decreaseCreditNotes.trim()) {
      alert('Reason is required to decrease credits');
      return;
    }

    setSavingDecreaseCredits(true);
    try {
      await api.post(`/super-admin/companies/${selectedCompany.id}/decrease_credits/`, {
        amount,
        source_transaction_id: decreaseSourceTransactionId,
        notes: decreaseCreditNotes.trim(),
      });
      setDecreaseCreditDialog(false);
      setDecreaseCreditAmount('');
      setDecreaseCreditNotes('');
      setDecreaseSourceTransactionId('');
      fetchOverview(selectedCompany.id);
    } catch (e: unknown) {
      const data = (e as { response?: { data?: Record<string, unknown> | string } }).response?.data;
      if (data && typeof data === 'object') {
        const errMsg = data.error;
        alert(typeof errMsg === 'string' ? errMsg : JSON.stringify(data));
      } else {
        alert(e instanceof Error ? e.message : 'Failed to decrease credits');
      }
    } finally {
      setSavingDecreaseCredits(false);
    }
  };

  // Memoized aggregations
  const sourceAdditionTransactions = useMemo(() => {
    if (!overview) return [];
    return overview.credit_transactions.filter(
      (t) => t.transaction_type === 'addition' && !t.is_expired && t.amount > 0
    );
  }, [overview]);

  // People aggregation
  const allRelationships = useMemo(() => {
    if (!overview) return [];
    const map = new Map<number, RelationshipRow>();
    overview.company_admins.forEach((r) => map.set(r.user.id, r));
    overview.employees.forEach((r) => map.set(r.user.id, r));
    overview.candidates.forEach((r) => {
      if (!map.has(r.user.id)) map.set(r.user.id, r);
    });
    return Array.from(map.values());
  }, [overview]);

  // Filtered People list
  const filteredPeople = useMemo(() => {
    if (!overview) return [];
    let list = allRelationships;

    if (peopleFilter === 'admin') {
      list = overview.company_admins;
    } else if (peopleFilter === 'employee') {
      list = overview.employees;
    } else if (peopleFilter === 'candidate') {
      list = overview.candidates;
    }

    if (peopleSearch.trim()) {
      const q = peopleSearch.toLowerCase();
      list = list.filter((r) => {
        const name = `${r.user.first_name} ${r.user.last_name}`.toLowerCase();
        const email = (r.user.email || '').toLowerCase();
        const mobile = (r.user.profile?.mobile || '').toLowerCase();
        return name.includes(q) || email.includes(q) || mobile.includes(q);
      });
    }

    return list;
  }, [overview, allRelationships, peopleFilter, peopleSearch]);

  const interviewTotal =
    overview?.interview_sessions_count ?? interviewTotalCount;

  const fetchInterviewPage = useCallback(async () => {
    if (!selectedCompany) return;
    setInterviewLoading(true);
    setInterviewListError(null);
    try {
      const params = new URLSearchParams({
        company: String(selectedCompany.id),
        page: String(interviewPage + 1),
        page_size: String(interviewRowsPerPage),
        ordering: '-started_at,-id',
      });
      if (interviewSearch.trim()) {
        params.append('search', interviewSearch.trim());
      }
      // Health issues are derived per row; filter that client-side on the page.
      if (interviewStatusFilter !== 'all' && interviewStatusFilter !== 'health_issues') {
        params.append('status', interviewStatusFilter);
      }
      const response = await api.get(`/super-admin/interview-sessions/?${params}`);
      const results = (response.data.results || response.data || []) as Array<
        InterviewRow & { job_description?: number | null }
      >;
      setInterviewRows(
        results.map((raw) => ({
          ...raw,
          job_description_id: raw.job_description_id ?? raw.job_description ?? null,
        })),
      );
      setInterviewTotalCount(
        typeof response.data.count === 'number' ? response.data.count : results.length,
      );
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      setInterviewListError(
        err.response?.data?.error || err.message || 'Failed to fetch interview sessions',
      );
      setInterviewRows([]);
      setInterviewTotalCount(0);
    } finally {
      setInterviewLoading(false);
    }
  }, [
    selectedCompany,
    interviewPage,
    interviewRowsPerPage,
    interviewSearch,
    interviewStatusFilter,
  ]);

  useEffect(() => {
    if (!selectedCompany || !overview) return;
    const timer = setTimeout(fetchInterviewPage, interviewSearch ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchInterviewPage, interviewSearch, interviewListRefreshKey, selectedCompany, overview]);

  // Reset paging when company changes.
  useEffect(() => {
    setInterviewPage(0);
    setInterviewSearch('');
    setInterviewStatusFilter('all');
  }, [selectedCompany?.id]);

  // Health filter is page-local; status/search paging is server-side.
  const visibleInterviews = useMemo(() => {
    if (interviewStatusFilter !== 'health_issues') return interviewRows;
    return interviewRows.filter(
      (i) =>
        !i.has_session_recording ||
        !i.has_feedback ||
        (i.unscored_question_count ?? 0) > 0,
    );
  }, [interviewRows, interviewStatusFilter]);

  const interviewLastPage = Math.max(
    0,
    Math.ceil(interviewTotalCount / interviewRowsPerPage) - 1,
  );
  const interviewSafePage = Math.min(interviewPage, interviewLastPage);
  useEffect(() => {
    if (interviewPage !== interviewSafePage) setInterviewPage(interviewSafePage);
  }, [interviewPage, interviewSafePage]);

  // Filtered Jobs
  const filteredJobs = useMemo(() => {
    if (!overview) return [];
    if (!jdSearch.trim()) return overview.job_descriptions;
    const q = jdSearch.toLowerCase();
    return overview.job_descriptions.filter(
      (j) => j.title.toLowerCase().includes(q) || (j.department_name || '').toLowerCase().includes(q)
    );
  }, [overview, jdSearch]);

  // Filtered Credits
  const filteredCredits = useMemo(() => {
    if (!overview) return [];
    if (!creditSearch.trim()) return overview.credit_transactions;
    const q = creditSearch.toLowerCase();
    return overview.credit_transactions.filter(
      (c) => (c.notes || '').toLowerCase().includes(q) || c.transaction_type.toLowerCase().includes(q)
    );
  }, [overview, creditSearch]);

  // Calculations for Overview Dashboard
  const creditSummary = overview?.credits_summary;
  const creditPercent = useMemo(() => {
    if (!creditSummary || creditSummary.total_credits <= 0) return 0;
    return Math.min(100, Math.round((creditSummary.remaining_credits / creditSummary.total_credits) * 100));
  }, [creditSummary]);

  const interviewStats = useMemo(() => {
    if (!overview) return { total: 0, completed: 0, completionRate: 0, avgScore: 0, issuesCount: 0 };
    const total = overview.interview_sessions_count ?? overview.interview_sessions.length;
    const completed =
      overview.interview_sessions_completed_count ??
      overview.interview_sessions.filter((s) => s.status === 'completed').length;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
    // Avg / health issue counts use the recent sample only (full scan is too
    // expensive for data-overview). The Interviews tab pages the full set.
    const sessions = overview.interview_sessions;
    const scoredSessions = sessions.filter((s) => s.effective_score != null && Number.isFinite(s.effective_score));
    const avgScore =
      scoredSessions.length > 0
        ? Math.round(scoredSessions.reduce((acc, s) => acc + (s.effective_score || 0), 0) / scoredSessions.length)
        : 0;
    const issuesCount = sessions.filter(
      (s) => !s.has_session_recording || !s.has_feedback || (s.unscored_question_count ?? 0) > 0
    ).length;

    return { total, completed, completionRate, avgScore, issuesCount };
  }, [overview]);

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      {/* -------------------------------------------------------------------
         Top Bar: Header & Interactive Company Selector
         ------------------------------------------------------------------- */}
      <Box sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ p: 1, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--primary-50)', color: 'var(--primary-700)', display: 'flex' }}>
                <HubIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.025em', lineHeight: 1.2 }}>
                  Company Hub
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  360° Administrative command center for active company management, talent, and credit allocation
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Quick Search Selector & All Companies Quick Toggle */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              minWidth: { xs: '100%', sm: 420, md: 520 },
              maxWidth: { xs: '100%', md: 560 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Autocomplete
                options={companies}
                loading={loadingCompanies}
                value={selectedCompany}
                onChange={(_, v) => {
                  setSelectedCompany(v);
                  if (v) {
                    localStorage.setItem(STORAGE_KEY, String(v.id));
                    sessionStorage.removeItem(STORAGE_KEY);
                  } else {
                    localStorage.removeItem(STORAGE_KEY);
                    sessionStorage.removeItem(STORAGE_KEY);
                  }
                  setActiveTab(0);
                }}
                getOptionLabel={(o) => `${o.name} (ID: ${o.id})`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px !important',
                    backgroundColor: '#ffffff !important',
                    border: '1.5px solid var(--border-default)',
                    boxShadow: '0 2px 6px -1px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.03)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    pr: '8px !important',
                    '&:hover': {
                      borderColor: 'var(--primary-400) !important',
                      boxShadow: '0 4px 12px -2px rgba(37, 99, 235, 0.12)',
                    },
                    '&.Mui-focused': {
                      borderColor: 'var(--primary-600) !important',
                      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.18)',
                    },
                    '& fieldset': {
                      border: 'none !important',
                    },
                  },
                  '& .MuiAutocomplete-input': {
                    fontSize: '0.875rem !important',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    py: '6.5px !important',
                  },
                }}
                renderOption={(props, option) => {
                  const isOptionActive = (option.status || 'active').toLowerCase() === 'active';
                  const isOptionSuspended = (option.status || '').toLowerCase() === 'suspended';
                  const statusLabel = isOptionActive ? 'Active' : isOptionSuspended ? 'Suspended' : 'Inactive';
                  const chipColor = isOptionActive ? 'success' : isOptionSuspended ? 'warning' : 'default';

                  return (
                    <li
                      {...props}
                      key={option.id}
                      style={{
                        padding: '8px 12px',
                        margin: '3px 6px',
                        borderRadius: '8px',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: '0.775rem',
                              fontWeight: 700,
                              background: isOptionActive
                                ? 'linear-gradient(135deg, #1e3a8a, #3b82f6)'
                                : 'linear-gradient(135deg, #64748b, #94a3b8)',
                              color: '#ffffff',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                          >
                            {option.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }} noWrap>
                              {option.name}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                          <Chip
                            size="small"
                            label={statusLabel}
                            color={chipColor as any}
                            variant={isOptionActive ? 'filled' : 'outlined'}
                            sx={{
                              height: 20,
                              fontSize: '0.675rem',
                              fontWeight: 600,
                              '& .MuiChip-label': { px: 0.8 },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.725rem',
                              color: 'var(--neutral-400)',
                              fontWeight: 600,
                            }}
                          >
                            #{option.id}
                          </Typography>
                        </Stack>
                      </Stack>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={
                      showAllCompanies
                        ? 'Search all companies...'
                        : 'Search active company...'
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '8px',
                              backgroundColor: 'var(--primary-50)',
                              color: 'var(--primary-600)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 0.5,
                              flexShrink: 0,
                            }}
                          >
                            <SearchIcon sx={{ fontSize: 16 }} />
                          </Box>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingCompanies ? <CircularProgress size={16} sx={{ color: 'var(--primary-500)', mr: 0.5 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* A very tiny button to show all companies */}
            <Tooltip
              title={
                showAllCompanies
                  ? 'Showing all companies. Click to filter to active only.'
                  : 'Currently showing active companies only. Click to show all companies.'
              }
              arrow
            >
              <Button
                size="small"
                variant={showAllCompanies ? 'contained' : 'outlined'}
                color={showAllCompanies ? 'primary' : 'inherit'}
                onClick={handleToggleShowAll}
                sx={{
                  height: 38,
                  px: 1.25,
                  fontSize: '0.725rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '10px',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                  minWidth: 'auto',
                  flexShrink: 0,
                  borderColor: showAllCompanies ? 'var(--primary-600)' : 'var(--border-default)',
                  backgroundColor: showAllCompanies ? 'var(--primary-700)' : 'var(--bg-surface)',
                  color: showAllCompanies ? '#ffffff' : 'var(--text-secondary)',
                  boxShadow: showAllCompanies ? '0 2px 4px rgba(37, 99, 235, 0.25)' : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'var(--primary-500)',
                    backgroundColor: showAllCompanies ? 'var(--primary-800)' : 'var(--neutral-100)',
                    color: showAllCompanies ? '#ffffff' : 'var(--primary-700)',
                    transform: 'translateY(-1px)',
                  },
                }}
                startIcon={
                  showAllCompanies ? (
                    <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                  ) : (
                    <PublicIcon sx={{ fontSize: '14px !important' }} />
                  )
                }
              >
                {showAllCompanies ? 'Showing All' : 'Show All'}
              </Button>
            </Tooltip>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>

      {/* -------------------------------------------------------------------
         Company Overview Banner & Credit Runway Widget
         ------------------------------------------------------------------- */}
      {selectedCompany && overview && (
        <Card sx={{ mb: 3.5, border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-sm)' }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
              {/* Left Identity Section */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      fontWeight: 800,
                      fontSize: '1.4rem',
                      background: 'var(--gradient-header)',
                      boxShadow: 'var(--shadow-sm)',
                    }}
                  >
                    {overview.company.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                      <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em', mr: 0.5 }}>
                        {overview.company.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={`ID: #${overview.company.id}`}
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                      />
                      <Chip
                        size="small"
                        label={formatLabel(overview.company.status)}
                        color={getStatusChipColor(overview.company.status)}
                      />
                      <Chip
                        size="small"
                        label={`${formatLabel(overview.company.subscription_plan)} Plan`}
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {overview.company.email || 'No primary contact email'} · {allRelationships.length} Total Users · {interviewTotal} Interview Sessions
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Middle Credit Health Gauge */}
              <Box sx={{ minWidth: { xs: '100%', sm: 340, md: 380 }, p: 2, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--neutral-50)', border: '1px solid var(--border-subtle)' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing="0.05em">
                    Credit Runway & Health
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color={creditPercent > 35 ? 'success.main' : creditPercent > 15 ? 'warning.main' : 'error.main'}>
                    {creditPercent}% Remaining
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={creditPercent}
                  color={creditPercent > 35 ? 'success' : creditPercent > 15 ? 'warning' : 'error'}
                  sx={{ height: 8, borderRadius: 'var(--radius-full)', mb: 1 }}
                />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={700} color="text.primary">
                    {creditSummary?.remaining_credits?.toLocaleString() ?? 0} <Typography component="span" variant="caption" color="text.secondary">/ {creditSummary?.total_credits?.toLocaleString() ?? 0} Total</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {creditSummary?.used_credits?.toLocaleString() ?? 0} Used · {formatExpiryDate(selectedCompany.latest_expiry_date)}
                  </Typography>
                </Stack>
              </Box>

              {/* Right Action Buttons */}
              <Stack direction={{ xs: 'row', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setCreditExpiryDate(getDefaultExpiryDate());
                    setCreditDialog(true);
                  }}
                >
                  Add Credits
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => {
                    setDecreaseCreditAmount('');
                    setDecreaseCreditNotes('');
                    setDecreaseSourceTransactionId('');
                    setDecreaseCreditDialog(true);
                  }}
                  disabled={sourceAdditionTransactions.length === 0}
                >
                  Adjust
                </Button>
                <Button variant="outlined" startIcon={<EditIcon />} onClick={openCompanyEdit}>
                  Edit
                </Button>
                <Tooltip title="Refresh overview data">
                  <IconButton
                    onClick={() => selectedCompany && fetchOverview(selectedCompany.id)}
                    disabled={loadingOverview}
                    sx={{ border: '1px solid var(--border-subtle)' }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {loadingOverview && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
          <CircularProgress size={44} />
        </Box>
      )}

      {/* -------------------------------------------------------------------
         Main 5-Tab Workspace
         ------------------------------------------------------------------- */}
      {!loadingOverview && selectedCompany && overview && (
        <Box>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            sx={{
              mb: 3,
              borderBottom: '2px solid var(--border-subtle)',
              '& .MuiTab-root': {
                fontSize: '0.95rem',
                fontWeight: 600,
                minHeight: 48,
              },
            }}
          >
            <Tab icon={<TrendingUpIcon fontSize="small" />} iconPosition="start" label="Overview" />
            <Tab
              icon={<PeopleIcon fontSize="small" />}
              iconPosition="start"
              label={`People & Team (${allRelationships.length})`}
            />
            <Tab
              icon={<AssessmentIcon fontSize="small" />}
              iconPosition="start"
              label={`Interviews (${interviewTotal})`}
            />
            <Tab
              icon={<WorkIcon fontSize="small" />}
              iconPosition="start"
              label={`Job Descriptions (${overview.job_descriptions.length})`}
            />
            <Tab
              icon={<ReceiptIcon fontSize="small" />}
              iconPosition="start"
              label={`Credit Ledger (${overview.credit_transactions.length})`}
            />
          </Tabs>

          {/* =================================================================
             Tab 0: Executive Overview & Analytics
             ================================================================= */}
          {activeTab === 0 && (
            <Box>
              {/* Pipeline Alerts */}
              {interviewStats.issuesCount > 0 && (
                <Alert
                  severity="warning"
                  icon={<WarningIcon />}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setActiveTab(2);
                        setInterviewStatusFilter('health_issues');
                      }}
                    >
                      View ({interviewStats.issuesCount})
                    </Button>
                  }
                  sx={{ mb: 3 }}
                >
                  {interviewStats.issuesCount} interview session{interviewStats.issuesCount === 1 ? ' has' : 's have'} pending recordings, unscored answers, or missing reports.
                </Alert>
              )}

              {/* KPI Cards Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2.5, mb: 4 }}>
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                          Total Credits
                        </Typography>
                        <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ mt: 0.5 }}>
                          {creditSummary?.total_credits?.toLocaleString() ?? 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {creditSummary?.remaining_credits?.toLocaleString() ?? 0} available balance
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--primary-50)', color: 'var(--primary-700)' }}>
                        <CreditCardIcon sx={{ fontSize: 28 }} />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                          Interview Sessions
                        </Typography>
                        <Typography variant="h4" fontWeight={800} color="info.main" sx={{ mt: 0.5 }}>
                          {interviewStats.total}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {interviewStats.completed} completed ({interviewStats.completionRate}%)
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--info-50)', color: 'var(--info-700)' }}>
                        <AssessmentIcon sx={{ fontSize: 28 }} />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                          Average Score
                        </Typography>
                        <Typography variant="h4" fontWeight={800} color="success.main" sx={{ mt: 0.5 }}>
                          {interviewStats.avgScore > 0 ? `${interviewStats.avgScore}%` : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Across all scored candidates
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--success-50)', color: 'var(--success-700)' }}>
                        <SchoolIcon sx={{ fontSize: 28 }} />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                          Job Descriptions
                        </Typography>
                        <Typography variant="h4" fontWeight={800} color="secondary.main" sx={{ mt: 0.5 }}>
                          {overview.job_descriptions.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {overview.job_descriptions.filter((j) => j.status === 'open').length} open positions
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--secondary-50)', color: 'var(--secondary-700)' }}>
                        <WorkIcon sx={{ fontSize: 28 }} />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>

              {/* Team Composition & Recent Activity Section */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
                {/* Team Roster Widget */}
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6" fontWeight={700}>
                        Team & Talent Roster
                      </Typography>
                      <Button size="small" onClick={() => setActiveTab(1)}>
                        Manage People
                      </Button>
                    </Stack>
                    <Stack spacing={2}>
                      <Box sx={{ p: 2, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--neutral-50)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>Company Admins</Typography>
                          <Typography variant="caption" color="text.secondary">Account managers & recruiters</Typography>
                        </Box>
                        <Chip label={overview.company_admins.length} color="primary" sx={{ fontWeight: 700 }} />
                      </Box>
                      <Box sx={{ p: 2, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--neutral-50)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>Company Employees</Typography>
                          <Typography variant="caption" color="text.secondary">Staff & team members</Typography>
                        </Box>
                        <Chip label={overview.employees.length} color="success" sx={{ fontWeight: 700 }} />
                      </Box>
                      <Box sx={{ p: 2, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--neutral-50)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>Candidates</Typography>
                          <Typography variant="caption" color="text.secondary">Interviewed & pipeline applicants</Typography>
                        </Box>
                        <Chip label={overview.candidates.length} color="info" sx={{ fontWeight: 700 }} />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Recent Interviews Widget */}
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6" fontWeight={700}>
                        Recent Interview Sessions
                      </Typography>
                      <Button size="small" onClick={() => setActiveTab(2)}>
                        View All ({interviewTotal})
                      </Button>
                    </Stack>
                    {overview.interview_sessions.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No interviews scheduled or completed yet.</Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {overview.interview_sessions.slice(0, 4).map((s) => (
                          <Box
                            key={s.id}
                            sx={{
                              p: 1.5,
                              borderRadius: 'var(--radius-md)',
                              border: '1px solid var(--border-subtle)',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              '&:hover': { backgroundColor: 'var(--bg-hover)' },
                            }}
                          >
                            <Box sx={{ minWidth: 0, mr: 1 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {s.candidate_name || s.candidate_email || `Session #${s.id}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {s.job_role || s.job_description_title || 'General'} · {formatDateTime(s.started_at)}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={formatLabel(s.status)} color={getInterviewStatusColor(s.status)} />
                              <IconButton size="small" onClick={() => setDetailSession(s)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}

          {/* =================================================================
             Tab 1: Unified People & Team Workspace
             ================================================================= */}
          {activeTab === 1 && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                {/* Search & Sub-Filter Bar */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 3 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={`All (${allRelationships.length})`}
                      clickable
                      color={peopleFilter === 'all' ? 'primary' : 'default'}
                      onClick={() => setPeopleFilter('all')}
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label={`Admins (${overview.company_admins.length})`}
                      clickable
                      color={peopleFilter === 'admin' ? 'primary' : 'default'}
                      onClick={() => setPeopleFilter('admin')}
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label={`Employees (${overview.employees.length})`}
                      clickable
                      color={peopleFilter === 'employee' ? 'primary' : 'default'}
                      onClick={() => setPeopleFilter('employee')}
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label={`Candidates (${overview.candidates.length})`}
                      clickable
                      color={peopleFilter === 'candidate' ? 'primary' : 'default'}
                      onClick={() => setPeopleFilter('candidate')}
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label={`Dependents (${overview.dependents.length})`}
                      clickable
                      color={peopleFilter === 'dependents' ? 'primary' : 'default'}
                      onClick={() => setPeopleFilter('dependents')}
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>

                  <TextField
                    size="small"
                    placeholder="Search people by name, email, mobile..."
                    value={peopleSearch}
                    onChange={(e) => setPeopleSearch(e.target.value)}
                    sx={{ width: { xs: '100%', md: 320 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'var(--neutral-400)' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>

                {peopleFilter === 'dependents' ? (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {overview.dependents_note || 'Family and dependent coverage records for employee benefits.'}
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                      Total dependent records: {overview.dependents.length}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer
                    sx={{
                      '&&': { overflowX: 'auto !important', overflowY: 'hidden !important' },
                      scrollbarWidth: 'thin',
                    }}
                  >
                    <Table size="small" sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                          <TableCell>User</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>User Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Mobile</TableCell>
                          <TableCell>Joined</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredPeople.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                No matching people found.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredPeople.map((r) => (
                            <TableRow key={r.id} hover>
                              <TableCell>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <Avatar sx={{ width: 32, height: 32, fontSize: '0.85rem', fontWeight: 700, backgroundColor: 'var(--primary-100)', color: 'var(--primary-800)' }}>
                                    {(r.user.first_name || r.user.username || 'U').charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {`${r.user.first_name} ${r.user.last_name}`.trim() || r.user.username}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      ID: #{r.user.id}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell>{r.user.email}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={formatLabel(r.role)}
                                  color={r.role === 'admin' ? 'primary' : r.role === 'employee' ? 'success' : 'default'}
                                />
                                {r.role === 'admin' && r.usermanager && (
                                  <Chip size="small" label="User Mgr" color="secondary" sx={{ ml: 0.5 }} />
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip size="small" label={formatLabel(r.user.user_type)} variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={r.user.is_active ? 'Active' : 'Inactive'}
                                  color={r.user.is_active ? 'success' : 'default'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                {r.user.profile?.mobile ? `${r.user.profile.country_code || ''} ${r.user.profile.mobile}` : '—'}
                              </TableCell>
                              <TableCell>{r.added_at ? new Date(r.added_at).toLocaleDateString() : '—'}</TableCell>
                              <TableCell align="right">
                                <Button size="small" startIcon={<EditIcon />} onClick={() => openUserEdit(r)}>
                                  Edit
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}

          {/* =================================================================
             Tab 2: Interviews & Assessment Workspace
             ================================================================= */}
          {activeTab === 2 && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                {/* Search & Quick Filter Controls */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 3 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={`All (${interviewTotal})`}
                      clickable
                      color={interviewStatusFilter === 'all' ? 'primary' : 'default'}
                      onClick={() => {
                        setInterviewStatusFilter('all');
                        setInterviewPage(0);
                      }}
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label="Completed"
                      clickable
                      color={interviewStatusFilter === 'completed' ? 'success' : 'default'}
                      onClick={() => {
                        setInterviewStatusFilter('completed');
                        setInterviewPage(0);
                      }}
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label="In Progress"
                      clickable
                      color={interviewStatusFilter === 'in_progress' ? 'info' : 'default'}
                      onClick={() => {
                        setInterviewStatusFilter('in_progress');
                        setInterviewPage(0);
                      }}
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label="Pending"
                      clickable
                      color={interviewStatusFilter === 'pending' ? 'warning' : 'default'}
                      onClick={() => {
                        setInterviewStatusFilter('pending');
                        setInterviewPage(0);
                      }}
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      icon={<WarningIcon fontSize="small" />}
                      label="Health Issues"
                      clickable
                      color={interviewStatusFilter === 'health_issues' ? 'error' : 'default'}
                      onClick={() => {
                        setInterviewStatusFilter('health_issues');
                        setInterviewPage(0);
                      }}
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      placeholder="Search candidate, role, ID..."
                      value={interviewSearch}
                      onChange={(e) => {
                        setInterviewSearch(e.target.value);
                        setInterviewPage(0);
                      }}
                      sx={{ width: { xs: '100%', md: 320 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: 'var(--neutral-400)' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Tooltip title="Refresh">
                      <IconButton
                        onClick={() => setInterviewListRefreshKey((k) => k + 1)}
                        color="primary"
                        aria-label="Refresh interviews"
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                {interviewStatusFilter === 'health_issues' && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Health Issues filters the current page of results only. Status and search run on the server.
                  </Typography>
                )}

                {interviewListError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {interviewListError}
                  </Alert>
                )}

                {interviewLoading && interviewRows.length === 0 ? (
                  <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress size={36} />
                  </Box>
                ) : (
                <TableContainer
                  sx={{
                    // Beat index.css `.MuiTableContainer-root { overflow: hidden !important }`
                    // so laptop widths can reach Actions without clipping columns.
                    '&&': { overflowX: 'auto !important', overflowY: 'hidden !important' },
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { height: 10 },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'rgba(30, 58, 138, 0.06)',
                      borderRadius: 5,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(30, 58, 138, 0.32)',
                      borderRadius: 5,
                      '&:hover': { backgroundColor: 'rgba(30, 58, 138, 0.5)' },
                    },
                  }}
                >
                  <Table
                    size="small"
                    sx={{ minWidth: 1100, borderCollapse: 'separate', borderSpacing: 0 }}
                  >
                    <TableHead>
                      <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                        <TableCell sx={stickyIdHeadSx}>ID</TableCell>
                        <TableCell sx={stickyCandidateHeadSx}>Candidate</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Job / JD</TableCell>
                        <TableCell>Score (Tot / Eff)</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Timeline</TableCell>
                        <TableCell>Pipeline Health</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleInterviews.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No matching interview sessions found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        visibleInterviews.map((s) => {
                            const elapsed = getElapsedSeconds(s);
                            return (
                              <TableRow key={s.id} hover>
                                <TableCell sx={stickyIdCellSx}>
                                  <Typography variant="body2" fontWeight={700}>
                                    #{s.id}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={stickyCandidateCellSx}>
                                  <Typography variant="body2" fontWeight={600}>
                                    {s.candidate_name || '—'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {s.candidate_email || (s.candidate ? `User #${s.candidate}` : 'No candidate')}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={formatLabel(s.status)}
                                    color={getInterviewStatusColor(s.status)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{s.job_role || s.job_description_title || '—'}</Typography>
                                  {s.job_description_id && (
                                    <Typography variant="caption" color="text.secondary">
                                      JD #{s.job_description_id}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Typography variant="body2" fontWeight={700} color={`${getScoreChipColor(s.total_score)}.main`}>
                                      {formatScore(s.total_score)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">/</Typography>
                                    <Typography variant="body2" fontWeight={700} color={`${getScoreChipColor(s.effective_score)}.main`}>
                                      {formatScore(s.effective_score)}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>
                                    {formatDurationClock(elapsed)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {formatDateTime(s.started_at)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="nowrap">
                                    <Tooltip title={s.has_session_recording ? 'Full video merged' : 'No merged recording'}>
                                      <Chip
                                        size="small"
                                        label={s.has_session_recording ? 'Recording' : 'No Video'}
                                        color={s.has_session_recording ? 'success' : 'warning'}
                                        variant="outlined"
                                      />
                                    </Tooltip>
                                    <Tooltip title={s.has_feedback ? 'Assessment report generated' : 'Report pending'}>
                                      <Chip
                                        size="small"
                                        label={s.has_feedback ? 'Report' : 'No Report'}
                                        color={s.has_feedback ? 'success' : 'warning'}
                                        variant="outlined"
                                      />
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                                    <Tooltip title="Inspect Details">
                                      <IconButton size="small" onClick={() => setDetailSession(s)}>
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Assessment Report">
                                      <IconButton size="small" onClick={() => setReportFor(toManageableSession(s))}>
                                        <AssessmentIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Post-interview feedback">
                                      <IconButton size="small" color="success" onClick={() => setFeedbackFor(toManageableSession(s))}>
                                        <AssignmentIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Recovery Actions">
                                      <IconButton size="small" onClick={() => setActionsFor(toManageableSession(s))}>
                                        <BuildIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit Session">
                                      <IconButton size="small" onClick={() => openSessionEdit(s)}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}

                <TablePagination
                  component="div"
                  count={interviewTotalCount}
                  page={interviewSafePage}
                  onPageChange={(_, p) => setInterviewPage(p)}
                  rowsPerPage={interviewRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setInterviewRowsPerPage(parseInt(e.target.value, 10));
                    setInterviewPage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              </CardContent>
            </Card>
          )}

          {/* =================================================================
             Tab 3: Job Descriptions Workspace
             ================================================================= */}
          {activeTab === 3 && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Company Job Descriptions ({overview.job_descriptions.length})
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Search roles or departments..."
                    value={jdSearch}
                    onChange={(e) => setJdSearch(e.target.value)}
                    sx={{ width: 300 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'var(--neutral-400)' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>

                <TableContainer
                  sx={{
                    '&&': { overflowX: 'auto !important', overflowY: 'hidden !important' },
                    scrollbarWidth: 'thin',
                  }}
                >
                  <Table size="small" sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                        <TableCell>Job Title</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredJobs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No job descriptions found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredJobs.map((j) => (
                          <TableRow key={j.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {j.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: #{j.id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={formatLabel(j.status)}
                                color={j.status === 'open' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{j.department_name || 'General'}</TableCell>
                            <TableCell>
                              <Chip size="small" label={j.jd_type || 'Standard'} variant="outlined" />
                            </TableCell>
                            <TableCell>{new Date(j.updated_at).toLocaleDateString()}</TableCell>
                            <TableCell align="right">
                              <Button size="small" startIcon={<EditIcon />} onClick={() => openJdEdit(j)}>
                                Edit
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* =================================================================
             Tab 4: Credit Ledger Workspace
             ================================================================= */}
          {activeTab === 4 && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Credit Ledger & Audit Trail
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Comprehensive log of all credit additions, usage deductions, and administrative adjustments
                    </Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Search notes or types..."
                    value={creditSearch}
                    onChange={(e) => setCreditSearch(e.target.value)}
                    sx={{ width: 300 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'var(--neutral-400)' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>

                <TableContainer
                  sx={{
                    '&&': { overflowX: 'auto !important', overflowY: 'hidden !important' },
                    scrollbarWidth: 'thin',
                  }}
                >
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                        <TableCell>Timestamp</TableCell>
                        <TableCell>Transaction Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Notes / Reference</TableCell>
                        <TableCell>Expiry Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCredits.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No credit transactions recorded.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredCredits.map((t) => (
                          <TableRow key={t.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {new Date(t.timestamp).toLocaleDateString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={formatLabel(t.transaction_type)}
                                color={
                                  t.transaction_type === 'addition'
                                    ? 'success'
                                    : t.transaction_type === 'usage' || t.transaction_type === 'expiry'
                                      ? 'error'
                                      : 'info'
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                fontWeight={800}
                                color={t.amount < 0 ? 'error.main' : 'success.main'}
                              >
                                {t.amount > 0 ? `+${t.amount}` : t.amount}
                              </Typography>
                            </TableCell>
                            <TableCell>{t.notes || '—'}</TableCell>
                            <TableCell>
                              {t.expiry_date ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="body2">{formatExpiryDate(t.expiry_date)}</Typography>
                                  {t.is_expired && <Chip size="small" label="Expired" color="error" variant="outlined" />}
                                </Stack>
                              ) : (
                                'No Expiry'
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Empty State when no company selected */}
      {!selectedCompany && !loadingCompanies && (
        <Card sx={{ textAlign: 'center', py: 8, px: 3, border: '1px dashed var(--border-default)' }}>
          <Box sx={{ maxWidth: 440, mx: 'auto' }}>
            <Box sx={{ p: 2, borderRadius: 'var(--radius-full)', backgroundColor: 'var(--primary-50)', color: 'var(--primary-600)', display: 'inline-flex', mb: 2 }}>
              <HubIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Select an Active Company to Begin
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Use the company selector at the top right to load administrative overview, credit ledger, candidate interviews, and staff rosters for any active company.
            </Typography>
          </Box>
        </Card>
      )}

      {/* -------------------------------------------------------------------
         Side-Over Inspection Drawer for Interview Sessions
         ------------------------------------------------------------------- */}
      <Drawer
        anchor="right"
        open={Boolean(detailSession)}
        onClose={() => setDetailSession(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 540 },
            p: 3.5,
            boxShadow: 'var(--shadow-2xl)',
          },
        }}
      >
        {detailSession && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Drawer Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={800}>
                    Session #{detailSession.id}
                  </Typography>
                  <Chip
                    size="small"
                    label={formatLabel(detailSession.status)}
                    color={getInterviewStatusColor(detailSession.status)}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {detailSession.candidate_name || 'Anonymous Candidate'} ({detailSession.candidate_email || 'No email'})
                </Typography>
              </Box>
              <IconButton onClick={() => setDetailSession(null)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {/* Drawer Content */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              <Stack spacing={3}>
                {/* Score Section */}
                <Box sx={{ p: 2, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--neutral-50)', border: '1px solid var(--border-subtle)' }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
                    Performance Scores
                  </Typography>
                  <Stack direction="row" spacing={3} alignItems="baseline" sx={{ mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Total Score</Typography>
                      <Typography variant="h4" fontWeight={800} color={`${getScoreChipColor(detailSession.total_score)}.main`}>
                        {formatScore(detailSession.total_score)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Effective Score</Typography>
                      <Typography variant="h4" fontWeight={800} color={`${getScoreChipColor(detailSession.effective_score)}.main`}>
                        {formatScore(detailSession.effective_score)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Details Grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Job Role</Typography>
                    <Typography variant="body2" fontWeight={600}>{detailSession.job_role || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Job Description</Typography>
                    <Typography variant="body2" fontWeight={600}>{detailSession.job_description_title || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatDurationClock(getElapsedSeconds(detailSession))}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Credits Consumed</Typography>
                    <Typography variant="body2" fontWeight={600}>{detailSession.credits_used ?? 0}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Started At</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatDateTime(detailSession.started_at)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Ended At</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatDateTime(detailSession.ended_at)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Interview Language</Typography>
                    <Typography variant="body2" fontWeight={600}>{detailSession.language_name || detailSession.language_code || 'English'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Verification</Typography>
                    <Typography variant="body2" fontWeight={600}>{detailSession.verification_status ? formatLabel(detailSession.verification_status) : '—'}</Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>

            {/* Drawer Actions */}
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1.5} justifyContent="flex-end" useFlexGap flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<AssessmentIcon />}
                onClick={() => {
                  setReportFor(toManageableSession(detailSession));
                  setDetailSession(null);
                }}
              >
                Report
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<AssignmentIcon />}
                onClick={() => {
                  setFeedbackFor(toManageableSession(detailSession));
                  setDetailSession(null);
                }}
              >
                Feedback
              </Button>
              <Button
                variant="outlined"
                startIcon={<BuildIcon />}
                onClick={() => {
                  setActionsFor(toManageableSession(detailSession));
                  setDetailSession(null);
                }}
              >
                Recovery
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  openSessionEdit(detailSession);
                  setDetailSession(null);
                }}
              >
                Edit
              </Button>
            </Stack>
          </Box>
        )}
      </Drawer>

      {/* -------------------------------------------------------------------
         Standard Action Dialogs (Company Edit, User Edit, Credits, JD, etc.)
         ------------------------------------------------------------------- */}
      {/* Edit Company Dialog */}
      <Dialog open={companyDialog} onClose={() => setCompanyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Company</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
          <TextField
            label="Company Name"
            value={companyForm.name}
            onChange={(e) => setCompanyForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Primary Email"
            value={companyForm.email}
            onChange={(e) => setCompanyForm((f) => ({ ...f, email: e.target.value }))}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={companyForm.status}
              onChange={(e) => setCompanyForm((f) => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Subscription Plan"
            value={companyForm.subscription_plan}
            onChange={(e) => setCompanyForm((f) => ({ ...f, subscription_plan: e.target.value }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setCompanyDialog(false)}>Cancel</Button>
          <Button onClick={saveCompany} disabled={savingCompany} variant="contained" startIcon={<SaveIcon />}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User Profile</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
          <TextField
            label="First Name"
            value={userForm.first_name}
            onChange={(e) => setUserForm((f) => ({ ...f, first_name: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Last Name"
            value={userForm.last_name}
            onChange={(e) => setUserForm((f) => ({ ...f, last_name: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Email"
            value={userForm.email}
            onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={userForm.is_active}
                onChange={(e) => setUserForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
            }
            label="Active User Account"
          />
          {userEdit?.role === 'admin' && (
            <FormControlLabel
              control={
                <Switch
                  checked={userForm.usermanager}
                  onChange={(e) => setUserForm((f) => ({ ...f, usermanager: e.target.checked }))}
                />
              }
              label="User Manager Role Privileges"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setUserDialog(false)}>Cancel</Button>
          <Button onClick={saveUser} disabled={savingUser} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Credits Dialog */}
      <Dialog open={creditDialog} onClose={() => setCreditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Credits to Company</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
          <TextField
            label="Amount"
            type="number"
            value={creditAmount}
            onChange={(e) => setCreditAmount(e.target.value)}
            fullWidth
            inputProps={{ min: 1 }}
            required
          />
          <TextField
            label="Expiry Date"
            type="date"
            value={creditExpiryDate}
            onChange={(e) => setCreditExpiryDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
            helperText="Credits will expire on this date (required)"
          />
          <TextField
            label="Notes / Reference"
            value={creditNotes}
            onChange={(e) => setCreditNotes(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setCreditDialog(false)}>Cancel</Button>
          <Button
            onClick={addCredits}
            disabled={savingCredits || !creditExpiryDate || !creditAmount}
            variant="contained"
          >
            Add Credits
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decrease Credits Dialog */}
      <Dialog open={decreaseCreditDialog} onClose={() => setDecreaseCreditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Decrease / Adjust Credits</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
          <TextField
            select
            label="Source addition transaction"
            value={decreaseSourceTransactionId}
            onChange={(e) =>
              setDecreaseSourceTransactionId(e.target.value ? parseInt(e.target.value, 10) : '')
            }
            fullWidth
            required
            helperText="Select the original credit addition transaction to deduct from"
          >
            {sourceAdditionTransactions.map((tx) => (
              <MenuItem key={tx.id} value={tx.id}>
                #{tx.id} · +{tx.amount} credits ({new Date(tx.timestamp).toLocaleDateString()})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Amount to Decrease"
            type="number"
            value={decreaseCreditAmount}
            onChange={(e) => setDecreaseCreditAmount(e.target.value)}
            fullWidth
            inputProps={{ min: 1 }}
            required
          />
          <TextField
            label="Reason / Notes"
            value={decreaseCreditNotes}
            onChange={(e) => setDecreaseCreditNotes(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            required
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDecreaseCreditDialog(false)}>Cancel</Button>
          <Button
            onClick={decreaseCredits}
            disabled={
              savingDecreaseCredits ||
              !decreaseCreditAmount ||
              !decreaseSourceTransactionId ||
              !decreaseCreditNotes.trim()
            }
            variant="contained"
            color="warning"
          >
            Decrease Credits
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Job Description Dialog */}
      <Dialog open={jdDialog} onClose={() => setJdDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Job Description</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
          <TextField
            label="Job Title"
            value={jdForm.title}
            onChange={(e) => setJdForm((f) => ({ ...f, title: e.target.value }))}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={jdForm.status}
              onChange={(e) => setJdForm((f) => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setJdDialog(false)}>Cancel</Button>
          <Button onClick={saveJd} disabled={savingJd} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Session Status Dialog */}
      <Dialog open={sessionDialog} onClose={() => setSessionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Interview Session</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={sessionForm.status}
              onChange={(e) => setSessionForm((f) => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={sessionForm.is_archived}
                onChange={(e) => setSessionForm((f) => ({ ...f, is_archived: e.target.checked }))}
              />
            }
            label="Archived Session"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setSessionDialog(false)}>Cancel</Button>
          <Button onClick={saveSession} disabled={savingSession} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* External Sub-Dialogs for Interview Session Actions & Report Viewer */}
      <SessionActionsDialog
        open={Boolean(actionsFor)}
        session={actionsFor}
        onClose={() => setActionsFor(null)}
        onSessionChanged={() => {
          setInterviewListRefreshKey((k) => k + 1);
          if (selectedCompany) fetchOverview(selectedCompany.id);
        }}
        onNotify={(message, severity) => setNotice({ message, severity })}
      />

      <SessionReportDialog
        open={Boolean(reportFor)}
        session={reportFor}
        onClose={() => setReportFor(null)}
      />

      <SessionFeedbackDialog
        open={Boolean(feedbackFor)}
        session={feedbackFor}
        onClose={() => setFeedbackFor(null)}
      />

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={notice?.severity ?? 'success'} onClose={() => setNotice(null)}>
          {notice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyHub;
